/**
 * @module  Experience/Softgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable: no-loop-func max-file-line-count*/
// Core Libraries
import { Component, OnInit, Input, Output, OnDestroy, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Wove } from 'aspect.js/';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Interfaces
import { BrowsePlanConfig } from '@core/aem/interfaces/shop/browse-plan.interface';
import { Location } from '@angular/common';
import {
  FamilyPlan,
  FamilyPlanResponse,
  FccLabelContent,
  GrandfatheredCategory,
} from '@experience/shop/softgoods/interfaces/family-plan.interface';
import { APIError } from '@support/api/common/error.interface';
import { PlanOption } from '@support/api/proxies/responses/api-2117.interface';
import {
  Cart,
  CartItem,
  CartResponse,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { CartLine } from '@support/api/proxies/responses/api-cart-response.interface';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { ConflictsAndWarnings } from '@experience/shop/shared-browse/interfaces/service-blade.interface';

// Services
import { ProductSelectorService } from '@experience/shop/product-selector/services/product-selector.service';
import { PurchasePathService } from '@experience/shop/services/purchase-path.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { BrowsePlanServiceService } from '@experience/shared-experience/services/browse-plan-service.service';
import { UserService } from '@support/shared/services/user.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { MatchmediaService } from '@core/public';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Constants
import {
  SHARED_BROWSE_COMPONENTS,
  SERVICES_AND_PLANS,
  SHARED_BROWSE_CONSTANTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import {
  PLAN_FAMILY_ROUTES_CONSTANTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { GLOBAL_CONSTANT } from '@support/shared/constants/app.constants';

// PDL
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { MixedCartService } from '@experience/shared-experience/services/mixed-cart.service';
import { CPS_CONSTANTS } from '@experience/change-plan-services/constants/change-plan-services.constants';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { throwError as observableThrowError, Subject } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { CART_EVENT_NAME, BUTTON_CONSTANTS } from '@support/shared/constants/pdl-data.constants';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
import { NavbarService } from '@root/shared/services/navbar.service';
import { SharedBrowseService } from '../../services/shared-browse.service';
import { PhoenixStateService } from '@root/shared/services/phoenix-state-service';

@Wove({ moduleName: SHARED_BROWSE_COMPONENTS.PLAN_FAMILY_MODULE })
@Component({
  selector: 'tmo-plan-family',
  templateUrl: './plan-family.component.html',
  styleUrls: [
    '../../../../../../style-sheets/modules/dashboard.scss',
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/html-blade-links.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
    '../../../../../../style-sheets/components/input-radio.component.scss',
  ],
})
export class PlanFamilyComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   *  stores change in zip code flag
   */
  public updateFamiyPlansData: boolean;

  /**
   *  check updateFamily plans
   */
  @Input()
  public set updateFamiyPlans(value: boolean) {
    this.updateFamiyPlansData = value;
    this.navigatedFromChangeEntry(this.updateFamiyPlansData);
  }

  /**
   * Stores value of Selected Family
   */
  public selectedFamily: number;

  /**
   *  stores value in case no plan needed
   */
  public isNoPlanNeeded: boolean;
  /**
   * Flag to check isAddLine
   */
  @Input() public isAddLine: boolean;
  /**
   *  softGoodsAEM content
   */
  @Input() public softGoodsAEMContent: object;
  /**
   * Flag to check showToolTip
   */
  @Input() public showToolTip: boolean;
  /**
   * Configurable AEM data passed from parent component
   */
  @Input() public aemContent: BrowsePlanConfig;
  /**
   * Flag to check whether prepaid
   */
  @Input() public isPrepaid: boolean;
  /**
   * Flag to check whether the plans are displayed on Product Selector
   */
  @Input() public isProductSelector?: boolean;
  /**
   * Flag to check wheater need to skip service section and directlty move to cart
   */
  @Input() public isSkipServiceNewFlow?: boolean;
  /**
   * no plan needed flag to disable plan blade
   */
  @Input() public disablePlanBlade?: boolean;
  /**
   * Flag to check if calling from CPS
   */
  @Input() public isCPS?: boolean;
  /**
   * Flag to check if billing address conflict
   */
  @Input() public isBillingAddressConflict?: boolean;
  /**
   * Flag to check if Account segmentation conflict
   */
  @Input() public isAccountSegmentationConflict?: boolean;
  /**
   * Emits selected plan
   */
  @Output() public selectedPlan: EventEmitter<PlanOption>;
  /**
   * Emits Selected plan option Id
   */
  @Output() public selectedPlanOptionId: EventEmitter<string>;

  /**
   * Emits noResultsError Emitter
   */
  @Output() public noResultsError: EventEmitter<boolean>;
  /**
   * Stores value of selected plan Id
   */
  @Input()
  public set selectedPlanOfferId(value: string) {
    this.selectedPlanId = value;
  }

  @Input() set noPlanNeeded(value: boolean) {
    this.isNoPlanNeeded = value;
    if (this.isNoPlanNeeded) {
      this.selectedPlanFamilyCode = null;
    } else if (this.isNoPlanNeeded === false) {
      this.selectedPlanFamilyCode = '';
    }
  }

  /**
   * Output variable to notify whether we are waiting for Plan API's response
   */
  @Output() public isResponseAwaited: EventEmitter<boolean>;

  /**
   * Flag to see open or close modal
   */
  public seeMoreModal: boolean;
  /**
   * model to store dynamic data
   */
  public modal: DynamicModal;

  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * Stores value of Selected Plan Name
   */
  public selectedPlanName: PlanOption;
  /**
   * Stores value to check Selected Plan
   */
  public planDeselected: boolean;
  /**
   * Stores value of cart Id
   */
  public cartId: string;
  /**
   * Condition to check change Entry
   */
  public changeEntry: boolean;
  /**
   * Condition to check seeMore Limit
   */
  public seeMore: number;
  /**
   * Stores value of Family Plan data
   */
  public familyPlanData: FamilyPlan[];
  /**
   * Stores boolean for whether checkbox is checked
   *
   * @type {boolean}
   * @memberof PlanFamilyComponent
   */
  public checked: boolean;
  /**
   * Stores boolean for whether checkbox is disabled
   * @type {boolean}
   * @memberof PlanFamilyComponent
   */
  public disabled: boolean;
  /**
   * Stores value of selected plan Id
   */
  public selectedPlanId: string;
  /**
   * Flag to check if assisted flow
   */
  // public isAssistedFlow: boolean;
  /**
   * Stores grandfathered plans for CPS flow
   */
  public grandFatheredPlans: GrandfatheredCategory;
  /**
   * variable to hold selected plan's family
   */
  public selectedPlanFamilyCode: string;
  /**
   * Constant to hold Grandfathered category name
   */
  public grandfatheredConst: string;
  /**
   * To check if the flow is authorized
   */
  public isAuthFlow: boolean;
  /**
   * Spinner controller
   */
  public showSpinner: boolean;
  /**
   * currentLine ID to be sent when adding plan to cart
   */
  public currentLineId: number;
  /**
   * To clear out all the subscribers
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * Stores API error message
   */
  public errorMessage: APIError[];
  /**
   * Flag to show no results error message
   */
  public noResults: Boolean;
  /**
   * Stores tourist plan constant
   */
  public touristPlan: string;

  /**
   * Stores focused tileId details
   */
  public focusedTileId: LineDeivceDetails;
  /**
   * Boolean to check if user is on phone
   */
  public isPhone: boolean;
  /**
   * Variable to hold conflicting services
   */
  public conflictingPlanServicesNotification: string;
  /**
   * Variable to check if it is add a fan flow
   */
  public isAddAFan: boolean;
  /**
   * Emits edited plan status
   */
  @Output() public isPlanEdited: EventEmitter<boolean>;

  /**
   * Emits Selected plan option Id
   */
  @Output() public isLimitedPlanSelected: EventEmitter<boolean>;
  /**
   * Variable to hold conflicting services
   */
  public conflictingActiveServices: CartItem[];
  /**
   * Stores selected plan name
   */
  public selectedPlanLabel: string;
  /**
   * Stores selected plan amount
   */
  public selectedAmount: string;
  /**
   * Stores if allowEdit is true
   */
  public mixedCartModal: StaticModal;
  /**
   * Stores customer id
   */
  public customerId: string;
  @Input() public allowEdit: boolean;
  public mixedCart: boolean;
  // public isWebChannel: boolean;
  public pdlData: object;
  public isModalNameNeeded: boolean;
  public tmobilePrepaidPlanOptions: PlanOption[];
  public connectByTmoPlanOptions: PlanOption[];
  public mobileInternetPlanOptions: PlanOption[];
  public id: string;
  public tmobilePrepaidPlanDetails: FamilyPlan;
  public mobileInternetPlanDetails: FamilyPlan;
  public isNewProductSelectorTemplateEnabled: boolean;
  public connectByTmoPlanDetails: FamilyPlan;
  public selectedFamilyId: string;
  @Output() public goToCart: EventEmitter<boolean>;
  @Output() public selectedPlanFamily: EventEmitter<string>;
  @Output() public showPlanSection: EventEmitter<boolean>;
  @Input() public set familyCode(value: string) {
    if (value) {
      this.selectedplanFamily(value);
    } else if (document.cookie.includes('brand=connect')) {
      this.selectedplanFamily(SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE);
    } else {
      this.selectedplanFamily(SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN);
    }
  }
  @Input() public digitalPlansView: boolean;

  public isEdit: boolean;
  /**
   * Shared browse Constants key value pairs
   */
  public sharedBrowseConstants: KeyValueMap;
  /**
   * Stores heading for selected plan
   */
  public planHeader: string;
  /**
   * flag to store wheater to show connect plans or not
   */
  public showConnectPlans: boolean = true;

  public isLoggedIn: boolean = false;

  public fccLabelLinkText: string;

  public fccLabelLinkUrl: string;

  @Input() public removeContinue: boolean;

  /**
   * Stores promotions for plan from AEM configuration
   */
  public planPromotions: { [key: string]: string } = {};
  public promotionTagIconTemplate: SafeHtml;

  @Input() public autopayDiscountDetailsModal: StaticModal;
  @Input() public autoPayDiscountEnabled: boolean;

  constructor(
    private purchasePathService: PurchasePathService,
    private productSelectorService: ProductSelectorService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private router: Router,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService,
    private browsePlanServiceService: BrowsePlanServiceService,
    private tileManagementService: TileManagementService,
    public routeSnapshot: ActivatedRoute,
    public userService: UserService,
    public matchMediaService: MatchmediaService,
    private commonUtilsService: CommonUtilService,
    private location: Location,
    private mixedCartService: MixedCartService,
    public pdlService: PdlService,
    public activatedRoute: ActivatedRoute,
    private pdpDataHandlerService: PdpDataHandlerService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private navBarService: NavbarService,
    private sharedBrowseService: SharedBrowseService,
    private sanitizer: DomSanitizer,
    private phoenixStateService: PhoenixStateService,
  ) {
    super(pdlService, activatedRoute);
    this.selectedPlan = new EventEmitter<PlanOption>();
    this.selectedPlanOptionId = new EventEmitter<string>();
    this.isResponseAwaited = new EventEmitter<boolean>();
    this.noResultsError = new EventEmitter<boolean>();
    this.isPlanEdited = new EventEmitter<boolean>();
    this.showPlanSection = new EventEmitter<boolean>();
    this.ngUnsubscribe = new Subject<void>();
    this.planDeselected = false;
    this.modal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
      modalName: 'plan-family-modal',
    };
    this.seeMoreModal = this.isAuthFlow = false;
    this.grandfatheredConst = SERVICES_AND_PLANS.GRANDFATHERED;
    this.touristPlan = SHARED_BROWSE_CONSTANTS.touristPlan;
    this.noResults = false;
    this.isNoPlanNeeded = false;
    this.isPhone = false;
    this.allowEdit = false;
    this.mixedCart = false;
    this.id = SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN;
    this.isLimitedPlanSelected = new EventEmitter<boolean>();
    this.selectedPlanFamily = new EventEmitter<string>();
    this.isNewProductSelectorTemplateEnabled =
      this.featureToggleUtilService.isNewProductSelectorTemplateEnabled();
    this.goToCart = new EventEmitter<boolean>();
    this.sharedBrowseConstants = SHARED_BROWSE_CONSTANTS;

    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() ===  CHANNELS.WEB;
  }

  /**
   * Loads the initial configuration of Plan-Family page
   * @returns void
   */
  public ngOnInit(): void {
    this.customerId = this.stateStoreUtilsService.getAuthCustomerId();
    this.isAddAFan = this.stateStoreUtilsService.getAddFan();
    this.sharedBrowseService.setPlanFamiliesContent();
    this.getUpdatedFocusedTileDetails();
    if (this.isCPS) {
      this.selectedPlanId = this.stateStoreUtilsService.getPlansPDPSelectedPlanId();
      const coreOfferFamilyName = this.stateStoreUtilsService.getCoreOfferFamilyName();
      this.selectedplanFamily(coreOfferFamilyName);
    }
    this.currentLineId = this.objectService.get(
      this.stateStoreUtilsService.getCart(),
      'linesOccupied',
      1
    );
    this.getFamilyPlansFromApi();
    this.changeEntry = this.updateFamiyPlansData;
    this.isPhone = this.matchMediaService.isPhone();
    if (this.customerId) {
      this.isAuthFlow = true;
    }
    this.seeMore = this.checkLimit();
    // this.isAssistedFlow = SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.WEB;
    this.createModal();
    this.isEdit = !!this.activatedRoute.snapshot.queryParams.isEdit;
    this.isLoggedIn = this.navBarService.isLoggedIn();
    this.showConnectPlans = document.cookie.includes('brand=connect') ? true : false;
    this.getPlanPromotionDetails();
  }

  /**
   * Method to destroy service data.
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.seeMoreModal = false;
    this.mixedCartService.planRequestBody = null;
    this.mixedCart = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public showSeeMoreModal(event?: EventEmitter<Object>): void {
    if (!this.isNewProductSelectorTemplateEnabled) {
      this.triggerEvent = event;
    }
    this.seeMoreModal = true;
    this.modal.toggleModalEmitter.emit({ open: true, close: false });
    // if (this.isWebChannel) {
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
    // }
  }

  /**
   * Method to handle Click event
   * @returns void
   */
  public closeModal(): void {
    if (!this.isNewProductSelectorTemplateEnabled) {
      const target = 'target';
      this.triggerEvent[target].focus();
    }
    this.modal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.seeMoreModal = false;
  }

  /**
   * Method to update the current selected plan
   * @returns void
   */

  public updateSelectedPlan(
    plan?: PlanOption,
    familyCode?: string,
    familyId?: string,
    planConfigurationId?: string,
    planOptionSelectorId?: string,
    selectedFamily?: number,
    overrideIndicator?: boolean
  ): void {
    this.conflictingActiveServices = [];
    this.conflictingPlanServicesNotification = '';
    this.errorMessage = null;
    if ((plan && this.selectedPlanId !== planOptionSelectorId) || this.mixedCart) {
      this.mixedCart = false;
      this.isPlanEdited.emit(true);
      if (!this.isNewProductSelectorTemplateEnabled) {
        this.selectedPlanId = planOptionSelectorId;
      }
      this.isAddAFan = false;
      this.stateStoreUtilsService.setPlansPDPSelectedPlanId(planOptionSelectorId);
      this.productSelectorService.changeRouteSelection(familyId);
      this.stateStoreUtilsService.setFamilyId(familyId);
      this.cartSupportService.announcePlanSelection(planConfigurationId);
      this.selectedPlanFamilyCode = familyCode;
      this.selectedPlanName = plan;
      this.selectedFamily = selectedFamily;
      if (!this.removeContinue) {
        this.selectedPlan.emit(plan);
      }
      this.selectedPlanOptionId.emit(planOptionSelectorId);
      if (this.isProductSelector) {
        let lineNumber;
        this.showSpinner = true;
        if (this.tileManagementService.isFocusedTileNonLineItem()) {
          lineNumber = this.tileManagementService.generateTile(lineTypeEnum.LINE, true).tileId;
        } else {
          lineNumber = this.stateStoreUtilsService.getFocusedTileId();
        }
        const transactionTypes = this.stateStoreUtilsService.getCustomerId()
          ? ORDER_TYPE.ADDALINE
          : ORDER_TYPE.ACTIVATION;
        this.cartSupportService
          .putSelectedPlanToCart(
            planOptionSelectorId,
            lineNumber,
            this.isProductSelector,
            transactionTypes,
            null,
            this.focusedTileId,
            overrideIndicator
          )
          .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
          .subscribe(
            (response: CartResponse) => {
              if (this.isNewProductSelectorTemplateEnabled) {
                this.selectedPlanId = planOptionSelectorId;
              }
              this.stateStoreUtilsService.setCartId(response.cart.cartId);
              const planId = this.objectService.get(this.focusedTileId, 'plansFromCart.id');
              const sku =
                this.objectService.get(this.focusedTileId, 'selectedDevice.sku') ||
                this.objectService.get(this.focusedTileId, 'byodSimData.simSKU');
              const existingDeviceId =
                this.objectService.get(this.focusedTileId, 'selectedDevice.cartItemId') ||
                this.objectService.get(this.focusedTileId, 'byodSimData.cartItemId');

              if (this.currentLineId === 1) {
                const familyCodeOfPrimaryLine = this.objectService.get(
                  this.familyPlanData,
                  `${this.selectedFamily}.familyCode`
                );
                this.stateStoreUtilsService.setPlanFamliyCodeOfPrimaryAccount(
                  familyCodeOfPrimaryLine
                );
              }
              if (response.errors && response.errors.length > 0) {
                this.errorMessage = response.errors;
              }
              this.checkSelectedPlan();
              if (this.tileManagementService.isFocusedTileNonLineItem()) {
                if (!planId && sku) {
                  this.updateDeviceToLineLevel(existingDeviceId, sku);
                } else {
                  this.tileManagementService.deleteLine();
                  this.tileManagementService.createTile(lineTypeEnum.LINE, false, null);
                  this.tileManagementService.mapToCartResponse(response.cart);
                  this.tileManagementService.getUpdatedData();
                  this.showSpinner = false;
                  this.routeToCart();
                }
              } else {
                this.errorMessage = null;
                this.showSpinner = false;
                this.tileManagementService.setIsPlanNeeded(false);
                this.tileManagementService.mapToCartResponse(response.cart);
                this.tileManagementService.getUpdatedData();
                this.triggerCartAddEvent();
                this.routeToCart();
                if (this.isSkipServiceNewFlow) {
                  this.routeToCart(true);
                }
              }
            },
            (error: APIError[]) => {
              this.showSpinner = false;
              this.noResults = true;
              this.errorMessage = error;
            }
          );
      } else if (this.isCPS && !this.stateStoreUtilsService.getAddFan()) {
        this.showSpinner = true;
        this.checkSelectedPlan();
        const lineId = this.stateStoreUtilsService.getLineId();
        this.cartSupportService
          .putSelectedPlanToCart(
            planOptionSelectorId,
            lineId,
            false,
            ORDER_TYPE.MANAGERATEPLAN,
            this.stateStoreUtilsService.getAccountId(),
            null,
            overrideIndicator,
            this.isBillingAddressConflict
          )
          .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
          .subscribe(
            (response: CartResponse) => {
              if (this.isNewProductSelectorTemplateEnabled) {
                this.selectedPlanId = planOptionSelectorId;
              }
              this.getConflictingServices(response.cart);
              this.showSpinner = false;
            },
            (error: APIError[]) => {
              if (error[0].code === 'BIZ_0001_INVALID_TRANS_TYPE') {
                this.updateModal();
                if (!this.mixedCartService.planRequestBody) {
                  this.mixedCartService.planRequestBody = {
                    plan: plan,
                    familyCode: familyCode,
                    familyId: familyId,
                    planConfigurationId: planConfigurationId,
                    planOptionSelectorId: planOptionSelectorId,
                    selectedFamily: selectedFamily,
                  };
                }
                this.mixedCart = true;
                this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
              }
              this.showSpinner = false;
              this.noResults = true;
              this.errorMessage = error;
            }
          );
      } else {
        this.selectedPlanId = planOptionSelectorId;
        this.checkSelectedPlan();
      }
    }
    // if (this.isWebChannel) {
    this.modal.modalName = plan.label;
    // }
  }

  /**
   * Function to move cart level device to line level device
   * @param {string} cartItemId
   * @param {string} sku
   */
  private updateDeviceToLineLevel(cartItemId: string, sku: string): void {
    const tempTileId: string = this.tileManagementService.generateTile(
      lineTypeEnum.LINE,
      true
    ).tileId;
    this.cartSupportService
      .updateCartLevelDeviceToLineLevel(cartItemId, sku, tempTileId)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (data: Cart) => {
          this.showSpinner = false;
          this.errorMessage = null;
          this.tileManagementService.deleteLine();
          this.tileManagementService.createTile(lineTypeEnum.LINE, false);
          this.tileManagementService.mapToCartResponse(data);
          this.tileManagementService.setNonLineItemDetails(data);
          this.tileManagementService.getUpdatedData();
          this.location.replaceState(
            PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR,
            this.focusedTileId.tileId
          );
          this.routeToCart();
          if (this.isSkipServiceNewFlow) {
            this.routeToCart(true);
          }
          this.triggerCartAddEvent();
        },
        (error: APIError[]) => {
          this.deleteLine(tempTileId, error);
        }
      );
  }

  private deleteLine(tempTileId: string, errorMessage: APIError[]): void {
    this.cartSupportService
      .deleteFocusedLine(tempTileId)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (data: Cart) => {
          this.errorMessage = errorMessage;
          this.selectedPlanFamilyCode = null;
          this.selectedPlanName = null;
          this.selectedFamily = null;
          this.selectedPlanId = null;
          this.showSpinner = false;
          this.tileManagementService.mapToCartResponse(data);
          this.tileManagementService.setNonLineItemDetails(data);
          this.tileManagementService.getUpdatedData();
        },
        (error: APIError[]) => {
          this.errorMessage = error;
          this.showSpinner = false;
        }
      );
  }

  public goToBrowsePlans(): void {
    this.router.navigate([PLAN_FAMILY_ROUTES_CONSTANTS.BROWSE_PLANS_PHONE], {
      queryParams: { allowEdit: true },
    });
  }

  /**
   * Method to check the limit of the detail showm for plan on desktop / Tablet.
   * @returns number
   */
  public checkLimit(): number {
    return this.purchasePathService.detailCharLimit();
  }

  /**
   * Loads family plan API data from service
   */
  public getFamilyPlansFromApi(): void {
    this.selectedFamily = null;
    let cacheObject: ApiCache;
    this.isResponseAwaited.emit(true);
    let lineIndex = 0;
    const tileModel = this.stateStoreUtilsService.getTileModelForTileId(
      this.stateStoreUtilsService.getTileIdInFocus()
    );
    const lineInFocus = this.objectService.get(tileModel, 'lineId');
    const cartLines = this.objectService.get(this.stateStoreUtilsService.getCart(), 'lines', 0);
    if (cartLines) {
      if (this.stateStoreUtilsService.isDefaultTileIdInFocus() || !lineInFocus) {
        lineIndex = cartLines.length;
      } else {
        const index = cartLines.findIndex((cartLine: CartLine) => lineInFocus === cartLine.id);
        lineIndex = index > 0 ? index : 0;
      }
    }
    if (!this.isCPS) {
      const cartReference: Cart = this.stateStoreUtilsService.getCart();
      const linesRemaining: number = this.objectService.get(cartReference, 'linesRemaining');
      const linesOccupied: number = this.objectService.get(cartReference, 'linesOccupied', 0);
      cacheObject = this.prepareCacheObjectForRequest(linesOccupied, linesRemaining);
    }
    this.showSpinner = true;
    const familyCode = this.stateStoreUtilsService.getPlanFamliyCodeOfPrimaryAccount();
    this.browsePlanServiceService
      .getBrowsePlans(
        this.isProductSelector,
        this.isAddLine,
        this.currentLineId,
        familyCode,
        this.isCPS,
        this.isBillingAddressConflict,
        this.isAccountSegmentationConflict,
        cacheObject
      )
      .pipe(
        catchError((err: APIError) => observableThrowError(err)),
        takeUntil(this.ngUnsubscribe.asObservable())
      )
      .subscribe(
        (plansData: FamilyPlanResponse) => {
          this.showSpinner = false;
          this.errorMessage = null;
          if (plansData.familyPlans.length === 0) {
            this.noResults = true;
          }

          if (plansData) {
            this.familyPlanData = plansData.familyPlans;
            this.grandFatheredPlans = plansData.grandFatheredPlans;
            this.checkIfProductSelectorPage(plansData);
            if (this.familyPlanData && !this.familyPlanData.length) {
              this.noResultsError.emit(true);
            } else {
              this.getFccBroadbandFacts();
            }
            this.selectedFamilyNamePlanOptions(this.familyPlanData);
            // @TODO commented as this was adding tileID even if it doesn't have a line
            // (line or non line item) in cart
            // this.softgoodsService.updateTiles(plansData.selectedLineId, this.isProductSelector);
            if (plansData.selectedPlan) {
              this.selectedPlanId = this.stateStoreUtilsService.getAddFan()
                ? ''
                : plansData.selectedPlan.planOptionSelectorId;
              this.selectedPlanFamilyCode = plansData.selectedPlanFamily || '';
              this.selectedPlanName = plansData.selectedPlan;
              this.stateStoreUtilsService.setPlansPDPSelectedPlanId(this.selectedPlanId);
              this.selectedPlan.emit(plansData.selectedPlan);
            }
            const cart = this.stateStoreUtilsService.getCart();
            if (cart && cart.cartId) {
              this.getConflictingServices(cart);
            }
            this.isResponseAwaited.emit(false);
            this.showPlanSection.emit(true);
            this.checkSelectedPlan();
          }
        },
        (err: APIError[]) => {
          this.errorMessage = err;
          this.showSpinner = false;
          this.isResponseAwaited.emit(false);
        }
      );
  }
  /**
   * Function tp prepare cache request
   * @param {number} linesOccupied
   * @param {number} linesRemaining
   * @returns {ApiCache}
   */
  private prepareCacheObjectForRequest(linesOccupied: number, linesRemaining: number): ApiCache {
    this.focusedTileId = this.stateStoreUtilsService.getFocusedTileLineNonLineItem();
    if (this.isProductSelector) {
      this.currentLineId = this.objectService.get(this.focusedTileId, 'plansFromCart.cartItemId')
        ? linesOccupied
        : linesOccupied + 1;
    } else {
      this.currentLineId =
        this.allowEdit && this.objectService.get(this.focusedTileId, 'plansFromCart.cartItemId')
          ? linesOccupied
          : linesOccupied + 1;
    }
    return {
      cacheKey: this.customerId || 'ANON',
      cacheResponse: true,
      preferCache: linesRemaining === 0 && linesOccupied > 1, // linesOccupied > 1 in case of single line
    };
  }
  /**
   * method to check which plan is selected
   * @returns void
   */
  private checkSelectedPlan(): void {
    (this.familyPlanData || []).forEach((family: FamilyPlan) => {
      family.planFamilySelected = false;
      family.planOptions.forEach((planOption: PlanOption) => {
        if (planOption.planOptionSelectorId === this.selectedPlanId) {
          this.selectedPlanLabel = planOption.label;
          this.selectedAmount =
            planOption.monthlyPrice && planOption.monthlyPrice.displayValue
              ? planOption.monthlyPrice.displayValue
              : '';
          family.planFamilySelected = true;
          this.selectedPlan.emit(planOption);
        }
      });
    });
  }

  /**
   * method to check which plan is selected
   * @returns void
   */
  public selectedFamilyNamePlanOptions(planFamily: FamilyPlan[]): void {
    (planFamily || []).forEach((family: FamilyPlan) => {
      switch (true) {
        case family.familyId === SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN:
          this.tmobilePrepaidPlanOptions = family.planOptions;
          this.tmobilePrepaidPlanDetails = family;
          this.selectedFamilyId = family.familyId;
          break;
        case family.familyId === SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE:
          this.connectByTmoPlanOptions = family.planOptions;
          this.connectByTmoPlanDetails = family;
          this.selectedFamilyId = family.familyId;
          break;
        case family.familyId === SHARED_BROWSE_CONSTANTS.MOBILE_INTERNET_PLAN:
          this.mobileInternetPlanOptions = family.planOptions;
          this.mobileInternetPlanDetails = family;
          this.selectedFamilyId = family.familyId;
          break;
        default:
          break;
      }
    });
    if (!!this.familyCode) {
      this.selectedplanFamily(SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN);
    }
  }

  /**
   * Loads the value of the state
   * @param selectedLink : stores the value of the selected lin
   * @returns void
   */
  public selectedplanFamily(idName?: string): void {
    this.id = idName;
    this.id === SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE
      ? this.isLimitedPlanSelected.emit(true)
      : this.isLimitedPlanSelected.emit(false);
    this.selectedPlanFamily.emit(idName);
    this.setPlanHeader(this.id);
    this.getFccBroadbandFacts();
  }
  /**
   * Navigating to plan details
   *
   * @param {FamilyPlan} planFamily
   * @memberof PlanFamilyComponent
   */
  public goToPlanDetail(familyCode: string): void {
    this.selectedPlanId = this.selectedPlanId || '';
    this.stateStoreUtilsService.setPlansPDPPreviousState(this.isCPS);
    this.router.navigate([PLAN_FAMILY_ROUTES_CONSTANTS.PLAN_DETAIL, familyCode], {
      queryParams: { allowEdit: this.allowEdit },
    });
  }
  /**
   * Loads New Plans when Input property value changes
   * @returns void
   */
  public navigatedFromChangeEntry(updateFamliyPlans: boolean): void {
    if (this.changeEntry !== updateFamliyPlans) {
      this.getFamilyPlansFromApi();
      this.changeEntry = updateFamliyPlans;
      this.selectedPlanFamilyCode = '';
    }
  }

  public handleClickEvent(event: Event): void {
    this.mixedCartModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    const key = 'id';
    if (event[key] === 'Continue') {
      const requestBody = this.mixedCartService.planRequestBody;
      this.updateSelectedPlan(
        requestBody.plan,
        requestBody.familyCode,
        requestBody.familyId,
        requestBody.planConfigurationId,
        requestBody.planOptionSelectorId,
        requestBody.selectedFamily,
        true
      );
    } else if (event[key] === 'ViewCart') {
      this.mixedCart = false;
      this.router.navigate([CPS_CONSTANTS.CART_URL]);
    }
    this.mixedCartService.planRequestBody = null;
  }
  /**
   * checks whether PS page or not and loads plans accordingly
   * @param {FamilyPlanResponse} plansData
   * @returns void
   */
  public checkIfProductSelectorPage(plansData: FamilyPlanResponse): void {
    // We need to only display 5 Family blades (at max) on Product Selector page. Below logic is to trim
    // extra blades and only display maximu 5 blades on PSP
    if (this.isProductSelector) {
      if (this.objectService.get(plansData, 'grandFatheredPlans.family', []).length) {
        if (
          this.objectService.get(plansData, 'familyPlans', []).length >=
          GLOBAL_CONSTANT.MAX_PLANS_BLADE_VISIBLE
        ) {
          const plans =
            plansData.familyPlans.slice(0, GLOBAL_CONSTANT.MAX_PLANS_BLADE_VISIBLE) || [];
          this.familyPlanData = plans;
          this.grandFatheredPlans = null;
        }
      } else {
        if (
          this.objectService.get(plansData, 'familyPlans', []).length >
          GLOBAL_CONSTANT.MAX_PLANS_BLADE_VISIBLE
        ) {
          const plans =
            plansData.familyPlans.slice(0, GLOBAL_CONSTANT.MAX_PLANS_BLADE_VISIBLE) || [];
          this.familyPlanData = plans;
        }
      }
    }
  }

  private getUpdatedFocusedTileDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(
        takeUntil(this.ngUnsubscribe.asObservable()),
        tap((data: LineDeivceDetails) => {
          if (this.selectedPlanFamilyCode !== this.grandfatheredConst) {
            this.selectedPlanFamilyCode = this.objectService.get(
              data,
              'plansFromCart.offerCode',
              null
            );
            this.selectedPlanId = this.objectService.get(data, 'plansFromCart.offerCode', null);
          }
          if (this.objectService.get(data, 'getUpdatedPlans', false)) {
            this.checkSelectedPlan();
            this.getFamilyPlansFromApi();
          } else if (
            this.objectService.get(data, 'tileId') !==
            this.objectService.get(this.focusedTileId, 'tileId')
          ) {
            this.checkSelectedPlan();
          }
        })
      )
      .subscribe((data: LineDeivceDetails) => {
        this.focusedTileId = data;
        this.isNoPlanNeeded = this.objectService.get(this.focusedTileId, 'noPlanNeeded');
      });
  }
  /**
   * Sets conflicted services conflicting with selected plan
   * @param {response} CartResponse
   */
  private getConflictingServices(response: Cart): void {
    this.conflictingActiveServices = this.cartSupportService.getConflictedServices(
      response,
      this.stateStoreUtilsService.getLineId()
    );
    const conflictingServicesOfferName = this.conflictingActiveServices
      .map((conflict: ConflictsAndWarnings) => conflict.offerName)
      .join(', ');
    this.conflictingPlanServicesNotification = this.commonUtilsService.messageFormatter(
      this.aemContent.hardConflictNotification,
      [conflictingServicesOfferName]
    );
  }

  private createModal(): void {
    this.mixedCartModal = {
      headerText: '',
      bodyText: '',
      disableDismiss: true,
      modalName: 'mixed-cart-modal',
      disableEsc: true,
      buttons: [
        {
          id: '',
          type: 'submit',
          name: '',
          class: 'btn btn-primary',
        },
        {
          id: 'ViewCart',
          type: 'cancel',
          name: '',
          class: 'btn btn-secondary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
  }

  /**
   * updates MixedcartModal
   * @returns void
   */
  private updateModal(): void {
    this.mixedCartModal.headerText = this.aemContent.clearYourCart;
    this.mixedCartModal.bodyText = this.aemContent.clearOrViewCart;
    this.mixedCartModal.buttons[0].id = this.mixedCartModal.buttons[0].name =
      this.aemContent.continue;
    this.mixedCartModal.buttons[1].name = this.aemContent.viewCart;
  }

  private triggerCartAddEvent(): void {
    // if (this.isWebChannel) {
    this.isModalNameNeeded = true;
    const cart = this.stateStoreUtilsService.getCart();
    const selectedPlanId = this.stateStoreUtilsService.getPlansPDPSelectedPlanId();
    const productInfo = this.pdpDataHandlerService.fetchProductInfo(cart, selectedPlanId);
    this.pdlData = {
      ctaName: BUTTON_CONSTANTS.SELECTPLAN,
      product: productInfo ? [productInfo[productInfo.length - 1]] : [],
    };
    const isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
    this.pdpDataHandlerService.triggerCartEvent(
      cart,
      this.pdlPageComponentId,
      isCartEmpty,
      CART_EVENT_NAME.CARTADD
    );
    // }
  }

  public openPlanDetailModal(selectedPlan: PlanOption): void {
    this.selectedPlanName = selectedPlan;
    this.showSeeMoreModal();
  }

  public routeToCart(goToCart?: boolean): void {
    if (this.isNewProductSelectorTemplateEnabled && goToCart) {
      this.goToCart.emit(true);
    }
  }

  private setPlanHeader(id: string): void {
    switch (true) {
      case id === SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN ||
        id === SHARED_BROWSE_CONSTANTS.TMO_PREPAID_CORE_OFFER_FAMILY_NAME:
        this.planHeader = this.objectService.get(
          this.aemContent,
          'tmoUnlmtRatePlans',
          SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN_HEADING
        );
        break;
      case id === SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE ||
        id === SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMO_CORE_OFFER_FAMILY_NAME:
        this.planHeader = this.objectService.get(
          this.aemContent,
          'connectByTmo',
          SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE_HEADING
        );
        break;
      case id === SHARED_BROWSE_CONSTANTS.MOBILE_INTERNET_PLAN ||
        id === SHARED_BROWSE_CONSTANTS.MOBILE_INTERNET_CORE_OFFER_FAMILY_NAME:
        this.planHeader = this.objectService.get(this.aemContent, 'mintPlans', '');
        break;
      default:
        break;
    }
  }

  public getFccBroadbandFacts(): void {
    const fccContent: FccLabelContent = this.sharedBrowseService.setFccBroadbandFacts(this.id);
    this.fccLabelLinkUrl = fccContent.fccLabelUrl;
    this.fccLabelLinkText = fccContent.fccLabelText;
  }

  public getPlanPromotionDetails(): void {
    this.planPromotions = this.sharedBrowseService.planFamiliesContent['planPromotions'];
    this.promotionTagIconTemplate = this.sanitizer.bypassSecurityTrustHtml(
      this.sharedBrowseService.promotionTagIconTemplate
    );
  }
  
  public toggleAutopayDiscountDetailsModal(openModal: boolean): void {
    this.autopayDiscountDetailsModal.toggleModalEmitter.emit({open: openModal, close: !openModal});
  }

}
